/**
 * IssuesDashboard.js — Autodesk Build-style Issue Log (table, filters, CRUD, batch delete).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

const API = '/technical-issues';

const ISSUE_TYPES = ['Quality', 'Safety', 'Design', 'Commissioning', 'Coordination', 'Other'];
const PRIORITIES = ['Low', 'Normal', 'High', 'Critical'];
const STATUSES = ['Draft', 'Open', 'In Progress', 'Resolved', 'Closed'];
const STATUS_FILTER_OPTIONS = ['All', 'Open', 'In Progress', 'Resolved', 'Closed'];

const STATUS_COLORS = {
  open: '#ef4444',
  'in progress': '#f59e0b',
  resolved: '#3b82f6',
  closed: '#16a34a',
  draft: '#9ca3af',
};

const PRIORITY_COLORS = {
  low: '#9ca3af',
  normal: '#3b82f6',
  high: '#ef4444',
  critical: '#dc2626',
};

const norm = (s) => String(s || '').trim().toLowerCase();

const statusBadgeStyle = (status) => {
  const bg = STATUS_COLORS[norm(status)] || '#e5e7eb';
  const color = norm(status) === 'draft' ? '#374151' : '#fff';
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    background: bg,
    color,
  };
};

const priorityBadgeStyle = (priority) => {
  const bg = PRIORITY_COLORS[norm(priority)] || '#9ca3af';
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    background: bg,
    color: '#fff',
  };
};

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(v);
  }
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(v);
  }
};

const emptyCreateForm = () => ({
  title: '',
  description: '',
  issue_type: 'Quality',
  category: '',
  priority: 'Normal',
  location: '',
  assigned_to: '',
  due_date: '',
});

const issueToEditForm = (row) => ({
  title: row.title || '',
  description: row.description || '',
  issue_type: row.issue_type || 'Quality',
  category: row.category || '',
  status: row.status || 'Open',
  priority: row.priority || 'Normal',
  root_cause: row.root_cause || '',
  location: row.location || '',
  assigned_to: row.assigned_to || '',
  due_date: row.due_date ? new Date(row.due_date).toISOString().slice(0, 10) : '',
  closed_date: row.closed_date ? new Date(row.closed_date).toISOString().slice(0, 10) : '',
  linked_sheet_id: row.linked_sheet_id != null ? String(row.linked_sheet_id) : '',
  linked_rfi_id: row.linked_rfi_id != null ? String(row.linked_rfi_id) : '',
});

const IssuesDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalIssues', projectId || 'global', true);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);

  const [editIssue, setEditIssue] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchIssues = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const r = await authenticatedApiCall(instance, accounts, 'GET', `${API}/project/${projectId}`);
      if (r.data?.success) setIssues(r.data.data || []);
      else showToast(r.data?.message || 'Failed to load issues', 'error');
    } catch (err) {
      console.error('Fetch issues error:', err);
      showToast('Failed to load issues', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir(field === 'created_at' || field === 'due_date' ? 'desc' : 'asc');
    }
  };

  const filteredSorted = useMemo(() => {
    let rows = [...issues];

    if (statusFilter !== 'All') {
      rows = rows.filter((r) => String(r.status || '').trim() === statusFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const hay = [
          r.title,
          r.description,
          r.category,
          r.assigned_to,
          r.location,
        ]
          .map((x) => String(x || '').toLowerCase())
          .join(' ');
        return hay.includes(q);
      });
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (r) => {
      switch (sortField) {
        case 'issue_number':
          return String(r.issue_number || '');
        case 'title':
          return String(r.title || '').toLowerCase();
        case 'issue_type':
          return String(r.issue_type || '').toLowerCase();
        case 'category':
          return String(r.category || '').toLowerCase();
        case 'status':
          return String(r.status || '').toLowerCase();
        case 'priority':
          return String(r.priority || '').toLowerCase();
        case 'assigned_to':
          return String(r.assigned_to || '').toLowerCase();
        case 'due_date':
          return r.due_date ? new Date(r.due_date).getTime() : 0;
        case 'created_at':
          return r.created_at ? new Date(r.created_at).getTime() : 0;
        default:
          return 0;
      }
    };

    rows.sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

    return rows;
  }, [issues, search, statusFilter, sortField, sortDir]);

  const toggleSelect = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredSorted.map((r) => r.id)));
  };

  const handleCreate = async () => {
    if (!createForm.title.trim()) {
      showToast('Title is required', 'error');
      return;
    }
    setCreating(true);
    try {
      const body = {
        title: createForm.title.trim(),
        description: createForm.description || null,
        issue_type: createForm.issue_type,
        category: createForm.category || null,
        status: 'Open',
        priority: createForm.priority,
        root_cause: null,
        location: createForm.location || null,
        assigned_to: createForm.assigned_to || null,
        due_date: createForm.due_date || null,
        linked_sheet_id: null,
        linked_rfi_id: null,
      };
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}`, body);
      if (r.data?.success) {
        setShowCreateModal(false);
        setCreateForm(emptyCreateForm());
        fetchIssues();
        showToast('Issue created');
      } else showToast(r.data?.message || 'Failed to create issue', 'error');
    } catch (err) {
      console.error('Create issue error:', err);
      showToast('Failed to create issue', 'error');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (row) => {
    setEditIssue(row);
    setEditForm(issueToEditForm(row));
  };

  const closeEdit = () => {
    setEditIssue(null);
    setEditForm({});
  };

  const parseNullableInt = (s) => {
    const t = String(s || '').trim();
    if (!t) return null;
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? null : n;
  };

  const handleSaveEdit = async () => {
    if (!editIssue?.id) return;
    setSavingEdit(true);
    try {
      const body = {
        title: editForm.title,
        description: editForm.description || null,
        issue_type: editForm.issue_type,
        category: editForm.category || null,
        status: editForm.status,
        priority: editForm.priority,
        root_cause: editForm.root_cause || null,
        location: editForm.location || null,
        assigned_to: editForm.assigned_to || null,
        due_date: editForm.due_date || null,
        closed_date: editForm.closed_date || null,
        linked_sheet_id: parseNullableInt(editForm.linked_sheet_id),
        linked_rfi_id: parseNullableInt(editForm.linked_rfi_id),
      };
      const r = await authenticatedApiCall(instance, accounts, 'PUT', `${API}/issue/${editIssue.id}`, body);
      if (r.data?.success) {
        closeEdit();
        fetchIssues();
        showToast('Issue updated');
      } else showToast(r.data?.message || 'Failed to update issue', 'error');
    } catch (err) {
      console.error('Update issue error:', err);
      showToast('Failed to update issue', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteOne = async () => {
    if (!editIssue?.id) return;
    if (!window.confirm('Delete this issue?')) return;
    try {
      const r = await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/issue/${editIssue.id}`);
      if (r.data?.success) {
        closeEdit();
        fetchIssues();
        showToast('Issue deleted');
      } else showToast(r.data?.message || 'Failed to delete', 'error');
    } catch (err) {
      console.error('Delete issue error:', err);
      showToast('Failed to delete issue', 'error');
    }
  };

  const handleBatchDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) {
      showToast('Select at least one issue', 'error');
      return;
    }
    if (!window.confirm(`Delete ${ids.length} selected issue(s)?`)) return;
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `${API}/project/${projectId}/batch-delete`, { ids });
      if (r.data?.success) {
        const n = selectedIds.size;
        setSelectedIds(new Set());
        fetchIssues();
        showToast(`Deleted ${n} issue(s)`);
      } else showToast(r.data?.message || 'Batch delete failed', 'error');
    } catch (err) {
      console.error('Batch delete error:', err);
      showToast('Failed to delete issue(s)', 'error');
    }
  };

  const headerBtn = (label, field) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        fontWeight: 600,
        color: '#111827',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {label}
      {sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </button>
  );

  const shellStyle = {
    fontFamily: 'system-ui, sans-serif',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 16,
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
  };

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };

  if (!projectId) {
    return (
      <div style={{ ...shellStyle, color: '#6b7280' }}>
        Select a project to view issues.
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 10000,
            padding: '12px 16px',
            borderRadius: 8,
            background: toast.type === 'error' ? '#dc2626' : '#16a34a',
            color: '#fff',
            fontSize: 14,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827', flex: '1 1 auto' }}>Issue Log</h2>
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
        <input
          type="search"
          placeholder="Search title, description, category, assignee, location…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: 320, flex: '1 1 200px' }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ ...inputStyle, maxWidth: 180, width: 'auto' }}
        >
          {STATUS_FILTER_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'All' ? 'All statuses' : s}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            setCreateForm(emptyCreateForm());
            setShowCreateModal(true);
          }}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 14px',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Create Issue
        </button>
        <button
          type="button"
          onClick={handleBatchDelete}
          disabled={!selectedIds.size}
          style={{
            background: selectedIds.size ? '#dc2626' : '#fca5a5',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 14px',
            fontWeight: 600,
            cursor: selectedIds.size ? 'pointer' : 'not-allowed',
            fontSize: 14,
          }}
        >
          Delete Selected ({selectedIds.size})
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '10px 8px', width: 40 }}>
                <input
                  type="checkbox"
                  checked={filteredSorted.length > 0 && filteredSorted.every((r) => selectedIds.has(r.id))}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedIds.size > 0 && !filteredSorted.every((r) => selectedIds.has(r.id));
                  }}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </th>
              <th style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{headerBtn('#', 'issue_number')}</th>
              <th style={{ padding: '10px 8px', minWidth: 180 }}>{headerBtn('Title', 'title')}</th>
              <th style={{ padding: '10px 8px' }}>{headerBtn('Type', 'issue_type')}</th>
              <th style={{ padding: '10px 8px' }}>{headerBtn('Category', 'category')}</th>
              <th style={{ padding: '10px 8px' }}>{headerBtn('Status', 'status')}</th>
              <th style={{ padding: '10px 8px' }}>{headerBtn('Priority', 'priority')}</th>
              <th style={{ padding: '10px 8px' }}>{headerBtn('Assigned To', 'assigned_to')}</th>
              <th style={{ padding: '10px 8px' }}>{headerBtn('Due Date', 'due_date')}</th>
              <th style={{ padding: '10px 8px' }}>{headerBtn('Created', 'created_at')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} style={{ padding: 24, color: '#6b7280' }}>
                  Loading…
                </td>
              </tr>
            ) : filteredSorted.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: 24, color: '#6b7280' }}>
                  No issues match your filters.
                </td>
              </tr>
            ) : (
              filteredSorted.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => openEdit(row)}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    background: selectedIds.has(row.id) ? '#eff6ff' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedIds.has(row.id)) e.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selectedIds.has(row.id) ? '#eff6ff' : 'transparent';
                  }}
                >
                  <td style={{ padding: '10px 8px' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={(e) => toggleSelect(row.id, e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: '10px 8px', color: '#374151', fontWeight: 500 }}>{row.issue_number || '—'}</td>
                  <td style={{ padding: '10px 8px', color: '#111827' }}>{row.title || '—'}</td>
                  <td style={{ padding: '10px 8px', color: '#4b5563' }}>{row.issue_type || '—'}</td>
                  <td style={{ padding: '10px 8px', color: '#4b5563' }}>{row.category || '—'}</td>
                  <td style={{ padding: '10px 8px' }}>
                    <span style={statusBadgeStyle(row.status)}>{row.status || '—'}</span>
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <span style={priorityBadgeStyle(row.priority)}>{row.priority || '—'}</span>
                  </td>
                  <td style={{ padding: '10px 8px', color: '#4b5563' }}>{row.assigned_to || '—'}</td>
                  <td style={{ padding: '10px 8px', color: '#4b5563' }}>{fmtDate(row.due_date)}</td>
                  <td style={{ padding: '10px 8px', color: '#6b7280' }}>{fmtDateTime(row.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => !creating && setShowCreateModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              maxWidth: 520,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24,
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Create Issue</h3>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={labelStyle}>Title *</label>
                <input style={inputStyle} value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Issue type</label>
                  <select
                    style={inputStyle}
                    value={createForm.issue_type}
                    onChange={(e) => setCreateForm({ ...createForm, issue_type: e.target.value })}
                  >
                    {ISSUE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <select
                    style={inputStyle}
                    value={createForm.priority}
                    onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <input style={inputStyle} value={createForm.category} onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Location</label>
                <input style={inputStyle} value={createForm.location} onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Assigned to</label>
                  <input style={inputStyle} value={createForm.assigned_to} onChange={(e) => setCreateForm({ ...createForm, assigned_to: e.target.value })} />
                </div>
                <div>
                  <label style={labelStyle}>Due date</label>
                  <input
                    type="date"
                    style={inputStyle}
                    value={createForm.due_date}
                    onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: creating ? 'wait' : 'pointer',
                }}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editIssue && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={closeEdit}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              maxWidth: 560,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 24,
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18 }}>Edit Issue</h3>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>
                  {editIssue.issue_number}
                  {editIssue.created_by ? ` · Created by ${editIssue.created_by}` : ''}
                </p>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={labelStyle}>Title *</label>
                <input style={inputStyle} value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Issue type</label>
                  <select
                    style={inputStyle}
                    value={editForm.issue_type}
                    onChange={(e) => setEditForm({ ...editForm, issue_type: e.target.value })}
                  >
                    {ISSUE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <select style={inputStyle} value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <input style={inputStyle} value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <select
                    style={inputStyle}
                    value={editForm.priority}
                    onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Due date</label>
                  <input
                    type="date"
                    style={inputStyle}
                    value={editForm.due_date}
                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Root cause</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                  value={editForm.root_cause}
                  onChange={(e) => setEditForm({ ...editForm, root_cause: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Location</label>
                <input style={inputStyle} value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Assigned to</label>
                <input style={inputStyle} value={editForm.assigned_to} onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Closed date</label>
                <input
                  type="date"
                  style={inputStyle}
                  value={editForm.closed_date}
                  onChange={(e) => setEditForm({ ...editForm, closed_date: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Linked sheet ID</label>
                  <input
                    style={inputStyle}
                    value={editForm.linked_sheet_id}
                    onChange={(e) => setEditForm({ ...editForm, linked_sheet_id: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Linked RFI ID</label>
                  <input
                    style={inputStyle}
                    value={editForm.linked_rfi_id}
                    onChange={(e) => setEditForm({ ...editForm, linked_rfi_id: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleDeleteOne}
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={savingEdit}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#2563eb',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: savingEdit ? 'wait' : 'pointer',
                  }}
                >
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IssuesDashboard;
